import { useEffect, useState } from 'react';

// Debug mode is enabled via VITE_DEBUG=true environment variable
const DEBUG = typeof import.meta !== 'undefined' && import.meta.env?.VITE_DEBUG === 'true';

export interface AIEatEvent {
  id: string;
  snakeName: string;
  beforeScore: number;
  afterScore: number;
  beforeRank: number;
  afterRank: number;
  timestamp: number;
}

interface DebugAIEatNotificationProps {
  events: AIEatEvent[];
  onClearEvent: (id: string) => void;
}

export const DebugAIEatNotification = ({ events, onClearEvent }: DebugAIEatNotificationProps) => {
  if (!DEBUG) return null;

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-1 max-h-[60vh] overflow-hidden pointer-events-none">
      {events.slice(-10).map((event) => (
        <DebugEventItem key={event.id} event={event} onExpire={() => onClearEvent(event.id)} />
      ))}
    </div>
  );
};

const DebugEventItem = ({ event, onExpire }: { event: AIEatEvent; onExpire: () => void }) => {
  const [opacity, setOpacity] = useState(1);

  useEffect(() => {
    // Fade out after 2 seconds
    const fadeTimer = setTimeout(() => {
      setOpacity(0);
    }, 2000);

    // Remove after fade completes
    const removeTimer = setTimeout(() => {
      onExpire();
    }, 2500);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(removeTimer);
    };
  }, [onExpire]);

  const rankChange = event.beforeRank - event.afterRank;
  const rankChangeText = rankChange > 0 
    ? `↑${rankChange}` 
    : rankChange < 0 
      ? `↓${Math.abs(rankChange)}` 
      : '→';

  // Format timestamp as HH:MM:SS
  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toTimeString().split(' ')[0]; // HH:MM:SS
  };

  return (
    <div
      className="px-3 py-1.5 rounded bg-black/80 border border-green-500/30 text-xs font-mono transition-opacity duration-500"
      style={{ opacity }}
    >
      <span className="text-white/50">[{formatTime(event.timestamp)}]</span>{' '}
      <span className="text-green-400">[DEBUG]</span>{' '}
      <span className="text-cyan-400 font-semibold">{event.snakeName}</span>{' '}
      <span className="text-white/70">ate:</span>{' '}
      <span className="text-yellow-400">{event.beforeScore}</span>
      <span className="text-white/50">→</span>
      <span className="text-yellow-300">{event.afterScore}</span>{' '}
      <span className="text-white/50">|</span>{' '}
      <span className="text-purple-400">#{event.beforeRank}</span>
      <span className="text-white/50">→</span>
      <span className={rankChange > 0 ? 'text-green-400' : rankChange < 0 ? 'text-red-400' : 'text-purple-300'}>
        #{event.afterRank} {rankChangeText}
      </span>
    </div>
  );
};
